import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation, useParams } from "react-router-dom";
const AdminLayout = lazy(() => import("./layouts/AdminLayout"));
const UserDashboardLayout = lazy(() => import("./layouts/UserDashboardLayout"));
import ScrollToTop from "./components/ScrollToTop";
import PageLoader from "./components/PageLoader";
import { RouteErrorBoundary } from "./components/ErrorBoundary";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { HomeLanguageProvider } from "./contexts/HomeLanguageContext";

// Public pages
const Index = lazy(() => import("./pages/Index"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const IDCardGenerator = lazy(() => import("./pages/IDCardGenerator"));
const IDCardMePage = lazy(() => import("./pages/IDCardMePage"));
const PublicProfile = lazy(() => import("./pages/PublicProfile"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const PaymentRefundServicePolicy = lazy(() => import("./pages/PaymentRefundServicePolicy"));
const UserPolicy = lazy(() => import("./pages/UserPolicy"));
const Support = lazy(() => import("./pages/Support"));
const Recovery = lazy(() => import("./pages/Recovery"));
const PayStatusPage = lazy(() => import("./pages/PayStatusPage"));
const RecoveryStatusPage = lazy(() => import("./pages/RecoveryStatusPage"));
const RecoveryVerifyNewEmailPage = lazy(() => import("./pages/RecoveryVerifyNewEmailPage"));
const Disconnected = lazy(() => import("./pages/Disconnected"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const AllShopsPage = lazy(() => import("./pages/AllShopsPage"));
const DownloadPage = lazy(() => import("./pages/DownloadPage"));

// Admin pages
const AdminDashboardView = lazy(() => import("./pages/AdminDashboardView"));
const AdminUsersPage = lazy(() => import("./pages/AdminUsersPage"));
const PanelUserDetailPage = lazy(() => import("./pages/PanelUserDetailPage"));
const VerificationQueuePage = lazy(() => import("./pages/VerificationQueuePage"));
const MembersListPage = lazy(() => import("./pages/MembersListPage"));
const MemberDetailPage = lazy(() => import("./pages/MemberDetailPage"));
const ShopsListPage = lazy(() => import("./pages/ShopsListPage"));
const ShopDetailPage = lazy(() => import("./pages/ShopDetailPage"));
const AdminShopClaimsPage = lazy(() => import("./pages/AdminShopClaimsPage"));
const AdminChangeRequestsPage = lazy(() => import("./pages/AdminChangeRequestsPage"));
const AdminPrintBatchPage = lazy(() => import("./pages/AdminPrintBatchPage"));
const AdminTicketCategoriesPage = lazy(() => import("./pages/AdminTicketCategoriesPage"));
const AdminTrashPage = lazy(() => import("./pages/AdminTrashPage"));
const AdminTicketsPage = lazy(() => import("./pages/AdminTicketsPage"));
const AdminTicketDetailPage = lazy(() => import("./pages/AdminTicketDetailPage"));
const AdminRecoveryPage = lazy(() => import("./pages/AdminRecoveryPage"));
const AdminAnnouncementsPage = lazy(() => import("./pages/AdminAnnouncementsPage"));
const AdminPollsPage = lazy(() => import("./pages/AdminPollsPage"));
const AdminAutomationsPage = lazy(() => import("./pages/AdminAutomationsPage"));
const AdminLogsPage = lazy(() => import("./pages/AdminLogsPage"));
const AdminEmailTemplatesPage = lazy(() => import("./pages/AdminEmailTemplatesPage"));
const AdminNfcLogsPage = lazy(() => import("./pages/AdminNfcLogsPage"));
const AdminDeviceStatusPage = lazy(() => import("./pages/AdminDeviceStatusPage"));
const PlansPricingPage = lazy(() => import("./pages/PlansPricingPage"));
const AdminCouponCodesPage = lazy(() => import("./pages/AdminCouponCodesPage"));
const AdminApprovalsPage = lazy(() => import("./components/dashboard/admin/AdminApprovalsPage"));
const SettingsPage = lazy(() => import("./components/dashboard/SettingsPage"));
const AdminExportsPage = lazy(() => import("./pages/AdminExportsPage"));
const AdminPermissionsPage = lazy(() => import("./pages/AdminPermissionsPage"));
const AdminTemplatesPage = lazy(() => import("./pages/AdminTemplatesPage"));

// Member pages
const MemberDashboardPage = lazy(() => import("./pages/MemberDashboardPage"));
const UserProfilePage = lazy(() => import("./pages/UserProfilePage"));
const UserInvoicesPage = lazy(() => import("./pages/UserInvoicesPage"));
const AnnouncementsInboxPage = lazy(() => import("./pages/AnnouncementsInboxPage"));
const MemberPollsPage = lazy(() => import("./pages/MemberPollsPage"));
const MyPlanPage = lazy(() => import("./pages/MyPlanPage"));
const RenewPage = lazy(() => import("./pages/RenewPage"));
const MyTicketsPage = lazy(() => import("./pages/MyTicketsPage"));
const MyTicketDetailPage = lazy(() => import("./pages/MyTicketDetailPage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const ActivityLogsPage = lazy(() => import("./pages/ActivityLogsPage"));
const ShopManagementPage = lazy(() => import("./pages/ShopManagementPage"));

/** Redirects /dashboard/pay?token=... or ?memberId=... to /renew, preserving query params for email payment links. */
const DashboardPayRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/renew${location.search}`} replace />;
};

/** Redirects /profile/:memberId to /user/:memberId for backwards compatibility. */
const ProfileRedirect = () => {
  const { memberId } = useParams<{ memberId: string }>();
  return <Navigate to={memberId ? `/user/${memberId}` : "/"} replace />;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry on 4xx client errors (auth handled by apiFetch)
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as { status?: number }).status;
          if (status && status >= 400 && status < 500) return false;
        }
        return failureCount < 2;
      },
    },
  },
});

// Protected Route Component (uses Auth context; shows loading while restoring session)
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { loading, isAuthenticated } = useAuth();
  if (loading) return <PageLoader />;
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <>{children}</>;
};

const ADMIN_PANEL_ROLES = ["superadmin", "admin", "manager", "tech_admin"] as const;

const NoAccess = () => {
  const { user, effectiveAccess } = useAuth();
  const hasAdminModules = (effectiveAccess?.adminModules?.length ?? 0) > 0;
  const isPanelRole = user?.role && ADMIN_PANEL_ROLES.includes(user.role as (typeof ADMIN_PANEL_ROLES)[number]);
  const dashboardPath = hasAdminModules || isPanelRole ? "/admin" : "/my-dashboard";
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
      <h2 className="text-xl font-semibold text-text-primary mb-2">
        You don&apos;t have access to this page
      </h2>
      <p className="text-sm text-text-muted mb-4">
        If you think this is a mistake, please contact your administrator to assign the right
        permissions.
      </p>
      <Link
        to={dashboardPath}
        className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium bg-primary text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
      >
        Go back to dashboard
      </Link>
    </div>
  );
};

const RequirePermission = ({
  children,
  permission,
  anyPermission,
  superAdminOnly = false,
}: {
  children: React.ReactNode;
  permission?: string;
  /** If set, user needs at least one of these permissions */
  anyPermission?: string[];
  superAdminOnly?: boolean;
}) => {
  const { user, hasPermission, loading } = useAuth();

  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (superAdminOnly && user.role !== "superadmin") return <NoAccess />;

  if (anyPermission?.length) {
    if (!anyPermission.some((p) => hasPermission(p))) return <NoAccess />;
  } else if (permission && !hasPermission(permission)) {
    return <NoAccess />;
  }

  return <>{children}</>;
};

/** Restrict to specific panel roles (e.g. NFC for superadmin/tech_admin). */
const RequireRole = ({
  children,
  allowedRoles,
}: {
  children: React.ReactNode;
  allowedRoles: Array<"superadmin" | "tech_admin" | "admin" | "manager" | "user">;
}) => {
  const { user, loading } = useAuth();

  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!allowedRoles.includes(user.role)) return <NoAccess />;
  return <>{children}</>;
};

/** Restrict by backend effectiveAccess.modules (module key). */
const RequireModule = ({
  children,
  moduleKey,
}: {
  children: React.ReactNode;
  moduleKey: string;
}) => {
  const { user, hasModule, loading } = useAuth();

  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasModule(moduleKey)) return <NoAccess />;
  return <>{children}</>;
};

/** Restrict by effectiveAccess.adminModules (admin panel only); use for /admin routes so USER (member-only) cannot access. */
const RequireAdminModule = ({
  children,
  moduleKey,
}: {
  children: React.ReactNode;
  moduleKey: string;
}) => {
  const { user, hasAdminModule, hasModule, loading, effectiveAccess } = useAuth();
  const useAdmin = effectiveAccess?.adminModules !== undefined;

  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  const allowed = useAdmin ? hasAdminModule(moduleKey) : hasModule(moduleKey);
  if (!allowed) return <NoAccess />;
  return <>{children}</>;
};

/** Allow access to admin layout only if user has at least one admin module; otherwise redirect to member dashboard. Panel roles (superadmin, admin, manager, tech_admin) are never redirected even when adminModules is empty. */
const RequireAdminPanel = ({ children }: { children: React.ReactNode }) => {
  const { user, effectiveAccess, loading } = useAuth();

  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  const adminModules = effectiveAccess?.adminModules;
  const isPanelRole = user.role && ADMIN_PANEL_ROLES.includes(user.role as (typeof ADMIN_PANEL_ROLES)[number]);
  if (adminModules !== undefined && adminModules.length === 0 && !isPanelRole) {
    return <Navigate to="/my-dashboard" replace />;
  }
  return <>{children}</>;
};

/** Home/landing is for guests only; redirect authenticated users to app. */
const HomeRoute = () => {
  const { loading, isAuthenticated, user } = useAuth();
  if (loading) return <PageLoader />;
  if (isAuthenticated) {
    const isAdminPanelUser = ADMIN_PANEL_ROLES.includes((user?.role ?? "") as (typeof ADMIN_PANEL_ROLES)[number]);
    return <Navigate to={isAdminPanelUser ? "/admin" : "/my-dashboard"} replace />;
  }
  return <Index />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <AuthProvider>
      <HomeLanguageProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
        <RouteErrorBoundary>
        <Routes>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/id-card/:memberId" element={<IDCardGenerator />} />
          <Route path="/user/:memberId" element={<PublicProfile />} />
          <Route path="/profile/:memberId" element={<ProfileRedirect />} />
          <Route path="/admin" element={<ProtectedRoute><RequireAdminPanel><AdminLayout /></RequireAdminPanel></ProtectedRoute>}>
            <Route index element={<RequireAdminModule moduleKey="dashboard"><AdminDashboardView /></RequireAdminModule>} />
            <Route path="users" element={<RequireAdminModule moduleKey="users"><AdminUsersPage /></RequireAdminModule>} />
            <Route path="users/:id" element={<RequireAdminModule moduleKey="users"><PanelUserDetailPage /></RequireAdminModule>} />
            <Route path="verification" element={<RequireAdminModule moduleKey="verification"><VerificationQueuePage /></RequireAdminModule>} />
            <Route path="approvals" element={<RequireAdminModule moduleKey="approvals"><AdminApprovalsPage /></RequireAdminModule>} />
            <Route path="members" element={<RequireAdminModule moduleKey="members"><MembersListPage /></RequireAdminModule>} />
            <Route path="members/:id" element={<RequireAdminModule moduleKey="members"><MemberDetailPage /></RequireAdminModule>} />
            <Route path="shops" element={<RequireAdminModule moduleKey="shops"><ShopsListPage /></RequireAdminModule>} />
            <Route path="shops/:id" element={<RequireAdminModule moduleKey="shops"><ShopDetailPage /></RequireAdminModule>} />
            <Route path="shop-claims" element={<RequireAdminModule moduleKey="shop-claims"><AdminShopClaimsPage /></RequireAdminModule>} />
            <Route path="change-requests" element={<RequireAdminModule moduleKey="change-requests"><AdminChangeRequestsPage /></RequireAdminModule>} />
            <Route path="print-batch" element={<RequireAdminModule moduleKey="print-batch"><AdminPrintBatchPage /></RequireAdminModule>} />
            <Route path="tickets" element={<RequireAdminModule moduleKey="tickets"><AdminTicketsPage /></RequireAdminModule>} />
            <Route path="tickets/:ticketCode" element={<RequireAdminModule moduleKey="tickets"><AdminTicketDetailPage /></RequireAdminModule>} />
            <Route path="ticket-categories" element={<RequireAdminModule moduleKey="ticket-categories"><AdminTicketCategoriesPage /></RequireAdminModule>} />
            <Route path="trash" element={<RequireAdminModule moduleKey="trash"><AdminTrashPage /></RequireAdminModule>} />
            <Route path="email-change-requests" element={<RequireAdminModule moduleKey="email-change-requests"><AdminRecoveryPage /></RequireAdminModule>} />
            <Route path="announcements" element={<RequireAdminModule moduleKey="announcements"><AdminAnnouncementsPage /></RequireAdminModule>} />
            <Route path="polls" element={<RequireAdminModule moduleKey="polls"><AdminPollsPage /></RequireAdminModule>} />
            <Route path="automations" element={<RequireAdminModule moduleKey="automations"><AdminAutomationsPage /></RequireAdminModule>} />
            <Route path="logs" element={<RequireAdminModule moduleKey="logs"><AdminLogsPage /></RequireAdminModule>} />
            <Route path="email-templates" element={<RequireAdminModule moduleKey="email-templates"><AdminEmailTemplatesPage /></RequireAdminModule>} />
            <Route
              path="nfc-logs"
              element={
                <RequireAdminModule moduleKey="nfc-logs">
                  <RequireRole allowedRoles={["superadmin", "tech_admin"]}>
                    <AdminNfcLogsPage />
                  </RequireRole>
                </RequireAdminModule>
              }
            />
            <Route
              path="device-status"
              element={
                <RequireAdminModule moduleKey="device-status">
                  <RequireRole allowedRoles={["superadmin", "tech_admin"]}>
                    <AdminDeviceStatusPage />
                  </RequireRole>
                </RequireAdminModule>
              }
            />
            <Route
              path="plans-pricing"
              element={
                <RequireAdminModule moduleKey="plans-pricing">
                  <RequireRole allowedRoles={["superadmin"]}>
                    <PlansPricingPage />
                  </RequireRole>
                </RequireAdminModule>
              }
            />
            <Route
              path="coupon-codes"
              element={
                <RequireAdminModule moduleKey="coupon-codes">
                  <RequireRole allowedRoles={["superadmin"]}>
                    <AdminCouponCodesPage />
                  </RequireRole>
                </RequireAdminModule>
              }
            />
            <Route
              path="settings"
              element={
                <RequireAdminModule moduleKey="settings">
                  <RequireRole allowedRoles={["superadmin"]}>
                    <SettingsPage />
                  </RequireRole>
                </RequireAdminModule>
              }
            />
            <Route
              path="exports"
              element={
                <RequireAdminModule moduleKey="exports">
                  <RequirePermission anyPermission={["VIEW_MEMBERS_LIST", "VIEW_MEMBERS_LIST_LITE", "VIEW_SHOPS_LIST", "VIEW_SHOPS_LIST_LITE", "EXPORTS_GLOBAL"]}>
                    <AdminExportsPage />
                  </RequirePermission>
                </RequireAdminModule>
              }
            />
            <Route
              path="permissions"
              element={
                <RequireRole allowedRoles={["superadmin", "tech_admin"]}>
                  <AdminPermissionsPage />
                </RequireRole>
              }
            />
            <Route
              path="templates"
              element={
                <RequireRole allowedRoles={["superadmin"]}>
                  <AdminTemplatesPage />
                </RequireRole>
              }
            />
          </Route>
          <Route path="/super-admin">
            <Route index element={<Navigate to="/admin" replace />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Route>
          <Route path="/admin-panel" element={<Navigate to="/admin" replace />} />
          {/* Payment links from emails: /dashboard/pay?token=... or ?memberId=... — redirect to /renew preserving params */}
          <Route path="/dashboard/pay" element={<DashboardPayRedirect />} />
          {/* Android app and shared links use /dashboard and /dashboard/* — align to admin panel */}
          <Route path="/dashboard">
            <Route index element={<Navigate to="/admin" replace />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Route>
          <Route path="/disconnected" element={<Disconnected />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/all-shops" element={<AllShopsPage />} />
          <Route path="/download" element={<DownloadPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsConditions />} />
          <Route path="/payment-policy" element={<PaymentRefundServicePolicy />} />
          <Route path="/user-policy" element={<UserPolicy />} />
          <Route path="/support" element={<Support />} />
          <Route path="/recovery" element={<Recovery />} />
          <Route path="/pay/status" element={<PayStatusPage />} />
          <Route path="/recovery/:code/status" element={<RecoveryStatusPage />} />
          <Route path="/recovery/:code/verify-new-email" element={<RecoveryVerifyNewEmailPage />} />
          <Route element={<ProtectedRoute><UserDashboardLayout /></ProtectedRoute>}>
            <Route path="my-dashboard" element={<RequirePermission anyPermission={["SELF_VIEW_DASHBOARD"]}><MemberDashboardPage /></RequirePermission>} />
            <Route path="my-dashboard/profile" element={<RequirePermission anyPermission={["SELF_EDIT_PROFILE", "SELF_VIEW_DASHBOARD"]}><UserProfilePage /></RequirePermission>} />
            <Route path="my-dashboard/invoices" element={<RequirePermission anyPermission={["SELF_VIEW_INVOICES"]}><UserInvoicesPage /></RequirePermission>} />
            <Route path="my-dashboard/announcements" element={<RequireModule moduleKey="announcements"><AnnouncementsInboxPage /></RequireModule>} />
            <Route path="my-dashboard/polls" element={<RequireModule moduleKey="polls"><MemberPollsPage /></RequireModule>} />
            <Route path="my-plan" element={<RequirePermission anyPermission={["SELF_VIEW_MY_PLAN", "SELF_PAY_RENEW"]}><MyPlanPage /></RequirePermission>} />
            <Route path="renew" element={<RequirePermission anyPermission={["SELF_PAY_RENEW"]}><RenewPage /></RequirePermission>} />
            <Route path="my-tickets" element={<RequirePermission anyPermission={["SELF_CREATE_TICKET", "SELF_VIEW_TICKET"]}><MyTicketsPage /></RequirePermission>} />
            <Route path="my-tickets/new" element={<RequirePermission anyPermission={["SELF_CREATE_TICKET", "SELF_VIEW_TICKET"]}><Support embedded /></RequirePermission>} />
            <Route path="my-tickets/:ticketCode" element={<RequirePermission anyPermission={["SELF_VIEW_TICKET"]}><MyTicketDetailPage /></RequirePermission>} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="activity-logs" element={<RequirePermission anyPermission={["SELF_VIEW_ACTIVITY_LOG"]}><ActivityLogsPage /></RequirePermission>} />
            <Route path="shop-management" element={<RequirePermission anyPermission={["SELF_MANAGE_SHOP"]}><ShopManagementPage /></RequirePermission>} />
            <Route path="id-card/me" element={<RequirePermission anyPermission={["SELF_DOWNLOAD_ID_CARD"]}><IDCardMePage /></RequirePermission>} />
          </Route>
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </RouteErrorBoundary>
        </Suspense>
      </BrowserRouter>
      </HomeLanguageProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
